<template>
  <div>
    <h1 class="mb-0">Pixel log</h1>
    <v-data-table
        :headers="table.headers"
        :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
        :items="getPixelLog.data"
        :server-items-length="getPixelLog.count"
        :disable-sort="true"
        :loading="table.loading"
        :footer-props="{itemsPerPageOptions: table.pageOptions}"
        :page="table.currentPage"
        :items-per-page="table.perPage"
        loading-text="Loading... Please wait"
        fixed-header
        :class="`elevation-1 co-data-table items-size-${table.heightCounter}`"
        item-class="td_title"
        color="#7F98A5"
        @pagination="updateTablePagination"
        :show-select="showSelect"
        @input="onSelect"
    >
      <template v-slot:top>
        <DataTableTopBar @search="search" :isDisplayTimer="true">
          <div slot="timePeriod">
            <v-row class="time_period_wrap align-center">
              <span class="time_period_title pl-3">Time Period</span>
              <v-col cols="12" lg="2">
                <v-select
                    v-model="timePeriodSelected"
                    item-value="id"
                    :items="timePeriodItems"
                    solo
                    dense
                    hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" lg="2" v-if="timePeriodSelected === 'Custom'">
                <v-menu
                    ref="rangeMenu"
                    v-model="rangeMenu"
                    :close-on-content-click="false"
                    :return-value.sync="timePeriodRange"
                    transition="scale-transition"
                    offset-y
                    max-width="320px"
                    min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="dateRangeText"
                        persistent-hint
                        readonly
                        prepend-inner-icon="mdi-calendar"
                        v-bind="attrs"
                        hide-details
                        dark
                        single-line
                        class="pt-0 mt-0"
                        label="Range"
                        v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                      v-model="timePeriodRange"
                      no-title
                      :close-on-content-click="false"
                      range
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="rangeMenu = false">Cancel</v-btn>
                    <v-btn text color="primary" @click="updateRange">OK</v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-spacer></v-spacer>
            </v-row>
          </div>
        </DataTableTopBar>
      </template>
      <template v-for="item of table.headers" :slot="`header.${[item.value]}`" slot-scope="{header}">
        <data-filter type="items"
                     v-if="table.filters.hasOwnProperty(header.value)"
                     :items="table.filters[header.value].data"
                     :entity="header.value"
                     :values="table.filters[header.value].selected"
                     @apply="submitFilter"
                     @search="updateDataInFilters"
        ></data-filter>
        <a v-if="header.sortable === undefined || header.sortable" @click="sortColumn(header.value)">
          <span class="header_title">{{header.text}}</span>
          <v-icon size="16" color="#7F98A5">mdi-arrow-{{header.value === table.orderBy ? table.sortColumnsAsc ? 'up' : 'down' : 'up-down'}}</v-icon>
        </a>
        <span v-else class="header_title">{{header.text}}</span>
      </template>
      <!-- header -->
      <template v-for="header of table.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
        <template v-if="header.value === 'dateAdded'">
          <span>{{ item.dateAdded }}</span>
        </template>
        <template v-else-if="header.value === 'url'">
          <v-tooltip top open-delay="400">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" @click="copyLink(item.url)">
                {{ item.url && item.url.length <= 25 ? item.url : item.url && item.url.substring(0, 25) + "..."  }}
              </span>
            </template>
            <span>{{item.url}}</span>
          </v-tooltip>
        </template>
        <template v-else-if="isEntityName(header.value)">
          <a class="entity-link" @click="showCard(item)">{{item[header.value]}}</a>
        </template>
        <template v-else>{{item[header.value]}}</template>
      </template>
      <!-- body -->
    </v-data-table>
    <DataTableBottomBar
        :showFilter="Object.values(table.filters).find(f => f.selected.length) !== undefined"
        @clearFilters="removeAllFilters"
        v-model="table.heightCounter"
    ></DataTableBottomBar>
    <EditProfile v-model="dialog.open" :item="dialog.item" :title="dialog.title"></EditProfile>
  </div>
</template>

<script>
import MainLayout from "../../layouts/MainLayout";
import {updateDate} from "@/helpers";
import EditProfile from "@/components/pages/affiliates/profiles/EditProfile";
import DataTableTopBar from '@/components/common/DataTableTopBar';
import DataTableBottomBar from '@/components/common/DataTableBottomBar';
import {mapActions, mapGetters} from 'vuex'
import store from '../../../store'
import pixelLogApi from "@/api/pixelLog";
import {debounce} from "debounce";

export default {
  props: {
    showSelect: false,
    showActions: {
      default: true
    }
  },
  data() {
    return {
      dialog: {
        open: false,
        item: {},
        title: 'New Item'
      },
      table: {
        heightCounter: 45,
        loading: false,
        searchInAffiliates: '',
        awaitingSearch: false,
        currentPage: 1,
        perPage: 25,
        pageChangedByMe: false,
        pageOptions: [10, 25, 50, 100],
        orderBy: '',
        sortColumnsAsc: true,
        headers: [
          { text: 'Id', value: 'id', class: 'color', align: 'center' },
          { text: 'Date', value: 'dateAdded', class: 'color', align: 'center' },
          { text: 'Offers', value: 'offerName', align: 'center' },
          { text: 'Advertiser', value: 'advertiserName', align: 'center' },
          { text: 'Rejected Reason', value: 'rejectedReason', align: 'center' },
          { text: 'IP Address', value: 'ip', align: 'center' },
          { text: 'Status', value: 'status', align: 'center' },
          { text: 'Url', value: 'url', align: 'center' },
        ],
        filters: {
          id: {selected: [], data: []},
          dateAdded: {selected: [], data: []},
          offerName: {selected: [], data: []},
          advertiserName: {selected: [], data: []},
          rejectedReason: {selected: [], data: []},
          ip: {selected: [], data: []},
          status: {selected: [], data: []},
          url: {selected: [], data: []},
        }
      },
      rangeMenu: false,
      timePeriodSelected: 'Today',
      timePeriodItems: ['Today', 'Yesterday', 'Week to date', 'Month to date', 'Year to day', 'Last week', 'Last month', 'Custom'],
      timePeriodRange: [new Date().toISOString().substr(0, 10), new Date().toISOString().substr(0, 10)],
      value: 0,
      editedItem: {},
    }
  },

  async mounted() {
    if (!this.showActions) {
      const actionsIndex = this.table.headers.findIndex(h => h.value === 'actions');
      if (actionsIndex > -1) {
        this.table.headers.splice(actionsIndex, 1)
      }
    }
    await this.getTableData()
    this.getFiltersData()
  },
  watch: {
    timePeriodSelected () {
      this.getTableData()
    },
  },

  methods: {
    ...mapActions('alert', ['ALERT_SUCCESS', 'ALERT_ERROR', 'ALERT_INFO', 'ALERT_WARNING', 'ALERT']),
    isEntityName(item) {
      return ['advertiserName'].includes(item)
    },
    showCard(item) {
      store.dispatch('cards/ADD', {
        type: 'advertiser',
        title: item.advertiserName,
        key: `advertiser-${item.id}`,
        data: item
      })
    },
    async save() {
      // call preloader
      this.loading = true;
      // call save profile store action
      try {
        await store.dispatch('pixelLog/savePixelLogStore', this.editedItem)
        this.ALERT_SUCCESS('Saved 👍');
        setTimeout(() => {
          this.ALERT_SUCCESS('');
        }, 6000);
        this.open = false;
      } catch (err) {
        this.ALERT_ERROR(err.message);
      } finally {
        this.loading = false;
      }
      // hide preloader
      this.$emit('save', this.editedItem)
    },
    debounceSearch: debounce(function (value) {
      this.table.searchInReports = value;
      this.getTableData();
    }, 700),
        getFiltersData() {
      const filters = {};
      for(const name in this.table.filters) {
        pixelLogApi.getFilter({
          filter: filters,
          name: name,
        }).then(res => {
          this.table.filters[name].data = res;
        });
      }
    },
    async getTableData() {
      this.table.loading = true
      this.activateFilters()
      const filters = await this.getFilters();
      const datePeriod = updateDate({typeDate: this.timePeriodSelected, fromDate: this.timePeriodRange[0], toDate: this.timePeriodRange[1]});

      await store.dispatch('pixelLog/savePixelLogStore', {
        payload: {
          fromDate: datePeriod.fromDate,
          toDate: datePeriod.toDate,
          limit: this.table.perPage,
          page: this.table.currentPage,
          orderBy: this.table.orderBy || '',
          orderDirectionAsc: this.table.sortColumnsAsc || false,
          filters: filters || null
        }
      })
      this.table.loading = false
    },
    /** todo add to helper */
    addParamsToLocation(params) {
      const query = JSON.stringify(params) === '{}' ? this.$route.path :
          this.$route.path + '?' + Object.keys(params).map(
          key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
          ).join('&')
      history.pushState({},null, query);
    },
    updateFiltersFromQuery() {
      const queryFilters = JSON.parse(this.$route.query.filters);
      for(const name in this.table.filters) {
        if(queryFilters[name]) {
          this.table.filters[name].selected = queryFilters[name];
        }
      }
    },
    async getFilters() {
      const filters = {};
      filters['search'] = (this.table.searchInAffiliates.length > 0) ? this.table.searchInAffiliates : '';
      for(const name in this.table.filters) {
        filters[name] = (this.table.filters[name].selected.length > 0) ?
            `'${this.table.filters[name].selected.join('\',\'')}'` :
            null;
      }

      return filters;
    },
    async submitFilter(data, entity) {
      this.table.currentPage = 1;
      this.table.pageChangedByMe = true;
      if(data && entity) {
        this.table.filters[entity].selected = data;
      }
      return await this.getTableData()
    },
    activateFilters() {
      const filtersForQuery = {};
      for (const name in this.table.filters) {
        if(this.table.filters[name].selected.length > 0) {
          filtersForQuery[name] = this.table.filters[name].selected;
        }
      }
      this.addParamsToLocation(
          JSON.stringify(filtersForQuery) !== '{}' ? {filters: JSON.stringify(filtersForQuery)} : {}
      );
    },
    async updateDataInFilters(data, modalName) {
      if(modalName) {
        pixelLogApi.getFilter({
          filter: this.table.filters,
          name: modalName,
          searchFilter: data
        }).then(res => {
          this.table.filters[modalName].data = res;
        });
      }
    },
    async sortColumn(column) {
      this.table.sortColumnsAsc = (this.table.orderBy === column) ? !this.table.sortColumnsAsc : true;
      this.table.orderBy = column
      await this.getTableData()
    },
    updateTablePagination(params){
      if (
          (this.table.currentPage === params.page &&
              this.table.perPage === params.itemsPerPage) ||
          this.table.pageChangedByMe
      ) {
        this.table.pageChangedByMe = false;
        return
      }
      this.table.currentPage = params.page
      this.table.perPage = params.itemsPerPage
      if(this.$route.query.filters) {
        this.updateFiltersFromQuery();
      }
      this.getTableData();
    },
    getCampainsUrl(item) {
      const filters = '?filters=' + encodeURI(JSON.stringify({aff_name_id: [item.affNameId]}))
      return '/affiliates/campaigns' + filters;
    },
    openDialog(item) {
      this.dialog.item = item;
      this.dialog.title = `Edit ${item.affNameId}'s profile`
      this.dialog.open = true;
    },
    search(value) {
      this.table.searchInAffiliates = value
      this.submitFilter();
    },
    isEditable(item) {
      const userSate = this.$store.state.user;
      if(!userSate || !userSate.user || !userSate.user[0]) {
        return false;
      }
      if (
          userSate.user[0].can('see_all_affiliates_pii') ||
          (userSate.user[0].can('see_own_affiliates_pii') && userSate.user[0].sfl_manager_id === item.affiliateManagerId)
      ) {
        return true
      }
    },
    async removeAllFilters() {
      for (let key in this.table.filters) {
        this.table.filters[key].selected = [];
      }
      await this.getTableData()
    },
    onSelect($event) {
      this.$emit('selected', $event)
    },
    updateRange() {
      this.$refs.rangeMenu.save(this.timePeriodRange)
      this.getTableData()
    },
    async copyLink(url) {
      this.ALERT_SUCCESS('URL Copied');
      await navigator.clipboard.writeText(url)
      setTimeout(() => {
        this.ALERT_SUCCESS('');
      }, 3500)
    },
  },

  computed: {
    ...mapGetters('pixelLog', ['getPixelLog']),
    dateRangeText () {
      return this.timePeriodRange.join(' - ')
    },
  },

  components: {
    MainLayout,
    EditProfile,
    DataTableTopBar,
    DataTableBottomBar
  },
}
</script>

<style lang="sass">
.co-data-table
  .v-data-table__wrapper
    .v-data-table-header
      th
        white-space: nowrap
.search_btn_wrap
  h1
    margin-bottom: 0
  .search_wrapper
    width: 250px
    padding-bottom: 15px
.v-data-table
  a.entity-link
    color: var(--v-primary-base)
</style>
